import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Button, CardActions, CardContent, makeStyles, Paper, useTheme } from '@material-ui/core'
import SendIcon from '@material-ui/icons/Send'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoDocumento, EnumTipoOperacion } from '../../../../api/oData/enums'
import { EnumRegulatoryReportState } from '../../../../api/oData/requests/enumRegulatoryReportState'
import { EnumFileStateWithDispatch } from '../../../../api/Pioneer/enums/enumFileStateWithDispatch'
import { NotificationMessageIcons } from '../../../../components'
import { ErrorDetailList } from '../../../../components/errorDetailList/errorDetailList'
import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import { generateIconsFromConfiguration } from '../../../../helpers/dataTableActionIconHelper'
import { enumToSelectOptionArray } from '../../../../helpers/enumHelper'
import { getUserPermissions } from '../../../../helpers/permissionsHelper'
import {
  FormDialogDefinitionProps,
  useErrorDetailListDialog,
  useFormDialog
} from '../../../../hooks'
import { useConfirmationDialog } from '../../../../hooks/useConfirmationDialog'
import { useHttpError } from '../../../../hooks/useHttpError'
import useRetries from '../../../../hooks/useRetries'
import { ActionIcons, GlobalNotification } from '../../../../model'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { Pages } from '../../../../model/RBAC/constants'
import { getCustomerDomain, notifyError } from '../../../../redux'
import { regulatoryReportDownloadCard, remitReportLngModel } from '../../regulatoryReports.Model'
import {
  fetchRegulatoryReportsRemitLngListItems,
  fetchReportFilesListItems,
  filterApplicableActionsConfigurationRemit,
  generateLng,
  getGenerateReportsLngRequest,
  getRRMSubjects,
  infoSendRemitTableMassive
} from '../../regulatoryReports.service'
import { styles } from '../../regulatoryReports.styles'
import { RegulatoryReportsDownloadCard } from '../../regulatoryReportsDownloadCard'

export interface RemitLngActivityProps {
  startDate: Date
  endDate: Date
  onFetching: (isFetching: boolean) => void
  onGlobalNotificationChange: (globalNotification: GlobalNotification | undefined) => void
}

export const RemitLngActivity: React.FC<RemitLngActivityProps> = props => {
  const [t] = useTranslation(['regulatoryReports', 'enums'])

  const { info } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [data, setData] = useState<remitReportLngModel[] | undefined>(undefined)

  const [dataDownloadCard, setdataDownloadCard] = useState<
    regulatoryReportDownloadCard[] | undefined
  >([])

  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()

  interface formDependenciesProps {
    formState: any
    rrm: SelectOption[]
  }

  const [formDependencies, setFormDependencies] = React.useState<formDependenciesProps>({
    formState: {},
    rrm: []
  })

  //#region Form definition

  const formDefinition = React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
    return {
      id: 'SEND',
      inputs: [
        {
          name: 'rrm.id',
          label: t('regulatoryReports:forms.rrm.label'),
          type: DataType.SELECT,
          selectOptions: formDependencies.rrm,
          readOnly: false,
          required: true
        }
      ]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDependencies])

  //#endregion

  //#region Effects

  const { showError, clearError, errorProps } = useHttpError()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const { openForm, updateFormDefinition, closeForm, setFormWaiting, formProps, CustomFormDialog } =
    useFormDialog()

  React.useEffect(() => {
    const fetchData = async () => {
      props.onFetching(true)
      fetchRegulatoryReports(props.startDate, props.endDate)
    }

    setData(undefined)
    fetchData()

    return () => void (isRetriesTimerActive && stopRetriesTimer())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.startDate, props.endDate])

  React.useEffect(() => {
    updateFormDefinition(formDefinition, formDependencies.formState)

    return () => void (isRetriesTimerActive && stopRetriesTimer())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefinition])

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async () => {
      setTimeout(async () => {
        await fetchRegulatoryReportFiles().then(response => {
          if (response !== null && response !== undefined && response.length === 0) {
            stopRetriesTimer()
            props.onGlobalNotificationChange(undefined)
            //info(t('regulatoryReports:globalNotifications.retries.messages.success'));
            return
          }
        })
      }, 5000)
    },
    5000,
    {
      autoStart: false,
      immediate: true,
      onRetry: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.LOADING,
          message: t('regulatoryReports:globalNotifications.retries.messages.onRetry'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.cancel'),
              onClick: () => {
                stopRetriesTimer()
                props.onGlobalNotificationChange({
                  message: t('regulatoryReports:globalNotifications.retries.messages.onStop'),
                  actions: [
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
                      onClick: async () => {
                        startRetriesTimer()
                      }
                    },
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.close'),
                      onClick: () => {
                        props.onGlobalNotificationChange(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.INFO,
          message: t('regulatoryReports:globalNotifications.retries.messages.onFinish'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            },
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.close'),
              onClick: () => {
                props.onGlobalNotificationChange(undefined)
              }
            }
          ]
        })
      },
      onError: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.ERROR,
          message: t('regulatoryReports:globalNotifications.retries.messages.onError'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            }
          ]
        })
      }
    }
  )

  //#endregion

  //#region  Functions

  const fetchRegulatoryReports = async (startDate: Date, endDate: Date) => {
    props.onFetching(true)
    await fetchRegulatoryReportsRemitLngListItems(startDate, endDate).then(response => {
      props.onFetching(false)
      if (response.success) {
        clearError()
        setData(response.data)
        fetchRegulatoryReportFiles()
      } else {
        response.error &&
          showError({
            title: t('regulatoryReports:title'),
            errorCode: response.error.code,
            onRefresh: async () => {
              fetchRegulatoryReports(startDate, endDate)
            }
          })
      }
    })
  }

  const fetchRegulatoryReportFiles = async (): Promise<regulatoryReportDownloadCard[] | null> => {
    return await fetchReportFilesListItems(EnumTipoDocumento.LngActivityReport).then(
      async response => {
        if (response.success && response.data?.length > 0) {
          setdataDownloadCard(
            response.data?.map((item: regulatoryReportDownloadCard) => {
              return completeDataTableActions(item)
            })
          )
          let updatedReportFiles: regulatoryReportDownloadCard[] = []
          response.data!.forEach((pendingReport: regulatoryReportDownloadCard) => {
            if (pendingReport.fileState === EnumFileStateWithDispatch.EnProceso) {
              updatedReportFiles.push(pendingReport)
            }
          })
          props.onFetching(false)
          return updatedReportFiles
        } else {
          props.onFetching(false)
          setdataDownloadCard([])
          return null
        }
      }
    )
  }

  const completeDataTableActions = (
    item: regulatoryReportDownloadCard
  ): regulatoryReportDownloadCard => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfigurationRemit(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    }
  }

  //#endregion

  //#region Handlers

  const onSendActionHandler = async (item: remitReportLngModel) => {
    if (infoSendRemitTableMassive([item])) {
      getConfirmation(ConfirmationDialogs.SEND_REMIT).then(async () => {
        closeConfirmation()
        await getRRMSubjects().then(response => {
          if (response.success && response.data?.length > 0) {
            if (response.data!.length === 1) {
              generateReport([item], response.data![0].value)
            } else {
              openFormHandler([item], response.data!)
            }
          } else {
            notifyError(t('regulatoryReports:forms.error'))
          }
        })
      })
    } else {
      await getRRMSubjects().then(response => {
        if (response.success && response.data?.length > 0) {
          if (response.data!.length === 1) {
            generateReport([item], response.data![0].value)
          } else {
            openFormHandler([item], response.data!)
          }
        } else {
          notifyError(t('regulatoryReports:forms.error'))
        }
      })
    }
  }

  const onSendMassiveActionHandler = async (items: remitReportLngModel[]) => {
    try {
      let tableHasAlreadySentOperations = infoSendRemitTableMassive(items)

      await getConfirmation(
        ConfirmationDialogs.GENERATE_MASSIVE_REMIT_REPORT,
        t('confirmDialogs:GENERATE_MASSIVE_REMIT_REPORT.message', {
          count: items.length,
          opt_message: tableHasAlreadySentOperations
            ? t('regulatoryReports:customMessages.GENERATE_MASSIVE_REMIT_REPORT')
            : ''
        })
      )

      closeConfirmation()

      await getRRMSubjects().then(response => {
        if (response.success && response.data?.length > 0) {
          if (response.data!.length === 1) {
            generateReport(items, response.data![0].value)
          } else {
            openFormHandler(items, response.data!)
          }
        } else {
          notifyError(t('regulatoryReports:forms.error'))
        }
      })
    } catch (error) {
      closeConfirmation()
    }
  }

  const openFormHandler = async (items: remitReportLngModel[], data: any) => {
    openForm({
      definition: formDefinition,
      title: t('regulatoryReports:forms.title'),
      mode: FormDialogMode.ADD,
      isWaiting: true,
      onClose: rrmId => onCloseForm(items, rrmId),
      okBtnLabel: 'Enviar'
    })

    setFormDependencies({
      ...formDependencies,
      formState: {},
      rrm: data
    })
    setFormWaiting(false)
  }

  const onCloseForm = async (items: remitReportLngModel[], data: any) => {
    if (data !== undefined) {
      closeForm()
      generateReport(items, data.rrm.id)
    } else {
      closeForm()
    }
  }

  const generateReport = async (items: remitReportLngModel[], rrmId: number) => {
    startRetriesTimer()
    var request = getGenerateReportsLngRequest(
      items,
      props.startDate,
      props.endDate,
      EnumTipoDocumento.LngActivityReport,
      rrmId
    )
    generateLng(request).then(async response => {
      if (response.success) {
        fetchRegulatoryReports(props.startDate, props.endDate)
        info(t('regulatoryReports:notifications.generateMessages.generateOK'))
      } else {
        response.error
          ? showErrorDetailListDialog(
              t('regulatoryReports:notifications.generateMessages.generateKO'),
              [response.error],
              moment().format('DD/MM/YYYY')
            )
          : notifyError(t('httpErrors:api.subtitle'))
      }
      fetchRegulatoryReportFiles()
      stopRetriesTimer()
    })
  }

  //#endregion

  //#region DataTable definition

  const columns: DataTableColumn[] = [
    {
      name: 'date',
      label: t('regulatoryReports:dataTable.columns.date'),
      type: DataType.DATE
    },
    {
      name: 'code',
      label: t('regulatoryReports:dataTable.columns.code'),
      type: DataType.TEXT,
      columnWidth: 30
    },
    {
      name: 'sequenceNumber',
      label: t('regulatoryReports:dataTable.columns.sequenceNumber'),
      type: DataType.TEXT,
      columnWidth: 30
    },
    {
      name: 'type',
      label: t('regulatoryReports:dataTable.columns.type'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoOperacion, 'enums:EnumTipoOperacion')
    },
    {
      name: 'destination',
      label: t('regulatoryReports:dataTable.columns.destination'),
      type: DataType.TEXT,
      columnWidth: 30
    },
    {
      name: 'ship',
      label: t('regulatoryReports:dataTable.columns.ship'),
      type: DataType.TEXT,
      columnWidth: 30
    },
    {
      name: 'state',
      label: t('regulatoryReports:dataTable.columns.state'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumRegulatoryReportState,
        'enums:EnumRegulatoryReportState'
      )
    }
  ]

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: false,
    print: false,
    selectableRows: false,
    canDelete: false,
    canEdit: false,
    canInsert: false,
    analytics: {
      customer: getCustomerDomain(),
      view: t('regulatoryReports:dataTable.analytics')
    },
    rowCustomActions: [
      {
        userPermission: UserPermission.EDIT || UserPermission.SEND,
        icon: <SendIcon fontSize="small" />,
        tooltip: t('regulatoryReports:dataTable.customActions.send'),
        onClick: onSendActionHandler
      }
    ],
    selectToolbarCustomActions: [
      {
        userPermission: UserPermission.EDIT || UserPermission.SEND,
        icon: <SendIcon fontSize="small" />,
        tooltip: t('regulatoryReports:dataTable.customActions.send'),
        onClick: onSendMassiveActionHandler
      }
    ],
    onFilterChange: (filter: ColumnFilter[]) => {
      //storeDataTableFilterForCurrentPage(filter);
    },
    onSearchChange: (search: string) => {
      //storeDataTableSearchForCurrentPage(search);
    }
  }

  //#endregion

  return (
    <>
      <Paper elevation={6}>
        <CardContent className={`${classes.cardContent} ${classes.cardContentPadding}`}>
          <DataTable
            data={data}
            columns={columns}
            options={options}
            info={DataTableErrorHandler(errorProps)}
            userPermissions={getUserPermissions(Pages.RegulatoryReport)}
            //appliedFilter={dataTableFilter}
            //appliedSearch={dataTableSearch}
          />
          <CardActions className={classes.remitActions}>
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={() => data && data.length > 0 && onSendMassiveActionHandler(data)}
              disabled={data === undefined || data.length === 0}
            >
              {t('regulatoryReports:components.remitLngActivity.onSendMassiveReportButton')}
            </Button>
          </CardActions>
        </CardContent>
      </Paper>
      <CustomFormDialog {...formProps}></CustomFormDialog>

      <RegulatoryReportsDownloadCard data={dataDownloadCard}></RegulatoryReportsDownloadCard>
      <ErrorDetailList {...errorDetailListDialogProps}></ErrorDetailList>
    </>
  )
}
